import { FormInputState } from 'src_common/common/mobx-utils/Form2/FormInputState';
import { makeAutoObservable, computed, action } from 'mobx';
import { getFieldsFromCriteria } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.helpers';
import { FormModel } from 'src_common/common/mobx-utils/Form2/FormModel';
import { validateNotEmpty } from 'src_common/common/mobx-utils/Form2/validators';
import {
    isMultiselectNotEmpty,
    isNumericBetween,
    isNumericWholeNumber,
    validateIf,
} from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.validators';
import { GamePickerState } from 'src/domains/casino/webview/bonus-engine/campaign/action-conditions/casino-sections/CasinoGamesPicker.state';
import { CampaignModel } from 'src/domains/casino/state/AppCasino/store/campaign-store/CampaignModel';
import { CampaignCreatorState } from 'src/domains/casino/webview/bonus-engine/campaign/CampaignCreator.state';
import { Common } from 'src/AppState/Common';

interface StakeType {
    value: 'min-spins' | 'min-monetary-value';
    label: string;
}
const stakeTypeOption: Array<StakeType> = [
    {
        value: 'min-spins',
        label: 'Minimum Rounds',
    },
    {
        value: 'min-monetary-value',
        label: 'Monetary Value',
    },
];

export type CasinoSelectionsFormType = {
    'min-monetary-value': string;
    'min-spins': string;
};

export class CasinoSelectionsState {
    public readonly criteriaForm: FormInputState<string, string>;
    public readonly numberOfSpins: FormInputState<string, string>;
    public readonly moneteryValue: FormInputState<string, string>;
    public readonly casinoResultForm: FormInputState<string, string>;
    public readonly gamePickerState: GamePickerState;
    public readonly form: FormModel<CasinoSelectionsFormType>;

    private readonly noEmptyValidationMessage = 'Required field';

    public constructor(
        public readonly common: Common,
        public readonly campaignId: string | undefined
    ) {
        makeAutoObservable(this);
        this.gamePickerState = new GamePickerState($appState.appCasinoState.gameStore, 'casino');
        this.criteriaForm = FormInputState.new('').map(validateNotEmpty(this.noEmptyValidationMessage));
        this.numberOfSpins = FormInputState.new('')
            .map((value) => {
                return validateIf(
                    () => this.criteriaForm.value === 'min-spins',
                    () => validateNotEmpty(this.noEmptyValidationMessage)(value)
                );
            })
            .map((value) => {
                return validateIf(
                    () => this.criteriaForm.value === 'min-spins',
                    () => isNumericWholeNumber(false, 'Enter a number. Minimum value is 1')(value)
                );
            })
            .map((value) => {
                return validateIf(
                    () => this.criteriaForm.value === 'min-spins',
                    () => isNumericBetween({ from: 1, to: 1000000 })(value)
                );
            });

        this.moneteryValue = FormInputState.new('')
            .map((value) => {
                return validateIf(
                    () => this.criteriaForm.value === 'min-monetary-value',
                    () => validateNotEmpty(this.noEmptyValidationMessage)(value)
                );
            })
            .map((value) => {
                return validateIf(
                    () => this.criteriaForm.value === 'min-monetary-value',
                    () => isNumericBetween({ from: 1, to: 1000000, currency: '£' })(value)
                );
            });

        this.casinoResultForm = FormInputState.new('').map((value) => {
            return validateIf(
                () => {
                    const campaignState = CampaignCreatorState.get(this.common, this.campaignId);
                    return campaignState.generalDetailsState.campaignTypeForm.value === 'settlement';
                },
                () => isMultiselectNotEmpty(false, this.noEmptyValidationMessage)(value)
            );
        });

        this.form = FormModel.group({
            'criteria-form': this.criteriaForm,
            'min-monetary-value': this.moneteryValue,
            'min-spins': this.numberOfSpins,
            'casino-result': this.casinoResultForm,
        });
    }

    @action public validateForm(): void {
        this.form.setAsVisited();
        this.casinoResultForm.setAsVisited();
    }

    @computed public get isFormValid(): boolean {
        return this.form.result.value.type === 'ok';
    }

    @computed public get formErrorsCount(): number {
        return this.form.errorsForView.length;
    }

    public isTouched(): boolean {
        return this.form.isTouched() || this.gamePickerState.isTouched();
    }

    public setIsTouched(): void {
        this.form.setIsTouched();
    }

    public resetIsTouched(): void {
        this.form.resetIsTouched();
        this.gamePickerState.resetIsTouched();
    }

    public resetIsVisited(): void {
        this.form.resetIsVisited();
    }

    @action public setFormValues = (campaignModel: CampaignModel): void => {
        const transformedCriteria = getFieldsFromCriteria(campaignModel);
        const minSpins = transformedCriteria?.['min-spins'] ?? '';
        const monetaryValue = transformedCriteria?.['min-monetary-value'] ?? '';

        this.numberOfSpins.setValue(minSpins);
        this.moneteryValue.setValue(monetaryValue);
        this.criteriaForm.setValue(
            transformedCriteria?.['min-spins'] === undefined ? 'min-monetary-value' : 'min-spins'
        );

        const criteria = campaignModel.actionConditions?.criteria;

        if (criteria === null || criteria === undefined) {
            return;
        }

        let isCasinoGamePresent = false;

        for (const item of criteria) {
            if (item.type === 'casino-game' && Array.isArray(item.value)) {
                isCasinoGamePresent = true;
                this.gamePickerState.setChecked(item.value);
            }

            if (item.type === 'casino-result' && Array.isArray(item.value)) {
                this.casinoResultForm.setValue(JSON.stringify(item.value));
            }
        }

        if (isCasinoGamePresent === false && this.gamePickerState.isEmpty() === false) {
            this.gamePickerState.clearChecked();
        }
    };

    public get allStakeTypeOptions(): Array<StakeType> {
        return stakeTypeOption;
    }
}
